import {
  programmeChecksQuery,
  programmeQuery,
} from "@common/api/maintenance-programme"
import type {
  ProgrammeDetailPublic,
  ProgrammeRevisionDetailPublic,
} from "@common/client"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Layers, Trash2 } from "lucide-react"
import {
  DetailPane,
  PaneMenu,
  PaneNumber,
} from "@/components/layout/detail-panel"
import { ListEmpty } from "@/components/layout/list-empty"
import { ExpandToggle, PaneStepper } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { revisionLabel } from "@/routes/_layout/maintenance-programmes/-lib/revision-label"
import { RevisionBadge } from "../-components/pane-heading"
import { PhaseForm } from "../-components/phase-form/phase-form"
import { PhaseTasks } from "../-components/phase-tasks"
import { editBlocker, useViewedRevision } from "../-lib/revision"
import { CHECKS } from "../-lib/search"
import { usePhaseOrder } from "../-lib/task-order"

export const Route = createFileRoute(
  "/_layout/maintenance-programmes_/$programmeId/phases/$phaseId",
)({
  component: PhasePane,
})

function PhasePane(): React.ReactElement {
  const { programmeId, phaseId } = Route.useParams()
  const { data: programme } = useSuspenseQuery(
    programmeQuery({ programme_id: programmeId }),
  )
  const revision = useViewedRevision(programme)
  const navigate = useNavigate()
  const close = () =>
    navigate({
      to: "/maintenance-programmes/$programmeId",
      params: { programmeId },
      search: true,
    })

  return revision ? (
    <RevisionPhasePane
      programme={programme}
      phaseId={phaseId}
      revision={revision}
      onClose={close}
    />
  ) : (
    <MissingPhase onClose={close} />
  )
}

function RevisionPhasePane({
  programme,
  phaseId,
  revision,
  onClose,
}: {
  programme: ProgrammeDetailPublic
  phaseId: string
  revision: ProgrammeRevisionDetailPublic
  onClose: () => void
}): React.ReactElement {
  const scope = { programme_id: programme.id, revision_id: revision.id }
  const { data: phases } = useSuspenseQuery(
    programmeChecksQuery({ ...scope, ...CHECKS }),
  )
  const canUpdate = useHasPermission("maintenance_programme:update")
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const phase = phases.data.find(({ id }) => id === phaseId)
  const blocked = editBlocker(programme, revision, canUpdate)
  const order = usePhaseOrder(scope)
  const index = order.indexOf(phaseId)
  const step = (offset: number) => {
    const target = index === -1 ? undefined : order[index + offset]
    if (target) {
      void navigate({
        to: "/maintenance-programmes/$programmeId/phases/$phaseId",
        params: { programmeId: programme.id, phaseId: target },
        search: true,
      })
    }
  }

  if (!phase) return <MissingPhase onClose={onClose} />

  return (
    <DetailPane
      title={phase.code}
      heading={
        <>
          <PaneNumber>{phase.code}</PaneNumber>
          <RevisionBadge revision={revision} />
        </>
      }
      actions={
        <PhasePaneActions
          index={index}
          total={order.length}
          onStep={step}
          onRemove={
            blocked === undefined
              ? () =>
                  navigate({
                    to: "/maintenance-programmes/$programmeId/phases/remove/$phaseId",
                    params: { programmeId: programme.id, phaseId },
                    search: true,
                  })
              : undefined
          }
        />
      }
      onClose={onClose}
    >
      <PhaseForm
        key={phase.id}
        scope={scope}
        phase={phase}
        blocked={blocked}
        onSaved={(saved) =>
          showToast(
            `Saved ${saved.code}`,
            revisionLabel(revision.number),
            "success",
          )
        }
        onCancel={onClose}
      >
        <PhaseTasks scope={scope} phaseId={phase.id} />
      </PhaseForm>
    </DetailPane>
  )
}

function PhasePaneActions({
  index,
  total,
  onStep,
  onRemove,
}: {
  index: number
  total: number
  onStep: (offset: number) => void
  onRemove: (() => void) | undefined
}): React.ReactElement {
  return (
    <>
      <PaneStepper
        noun="phase"
        index={index}
        total={total}
        onPrevious={index > 0 ? () => onStep(-1) : null}
        onNext={index !== -1 && index < total - 1 ? () => onStep(1) : null}
      />
      <PaneMenu label="Phase actions">
        {onRemove && (
          <DropdownMenuItem onSelect={onRemove}>
            <Trash2 />
            Remove from revision
          </DropdownMenuItem>
        )}
      </PaneMenu>
      <ExpandToggle />
    </>
  )
}

function MissingPhase({
  onClose,
}: {
  onClose: () => void
}): React.ReactElement {
  return (
    <DetailPane title="Phase" onClose={onClose}>
      <ListEmpty
        icon={Layers}
        title="Phase not in this revision"
        body="It may have been removed, or it belongs to another revision."
      />
    </DetailPane>
  )
}
